// src/pages/storeItems/forms/StoreItemForm.tsx

import React, { useEffect, useState } from 'react'
import { Field, getFormValues, initialize, InjectedFormProps, reduxForm } from 'redux-form'
import { Button, Col, Form, Popover, Row, Tag, Tooltip } from 'antd'
import { PlusOutlined, SaveOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import { FORMS, STORE_ITEM_TRANSACTION_TYPE } from '../../../utils/enums'
import TextField from '../../../atoms/form/TextField'
import validateForm from './validateStoreItemForm'
import { getStoreItem, IStoreItemForm } from '../../../reducers/storeItems/actions'

import CreateStoreItemTransactionModal, { CreateTransactionProps } from '../components/CreateStoreItemTransactionModal'
import AddressWithStocksTable, { getPriorityForStoreType } from '../components/AddressWithStocksTable'
import { colorHash } from '../StoreItems'
import { STORE_TYPE } from '../../stores/Stores'
import InputNumberField from '../../../atoms/InputNumberField'
import InputField from '../../../atoms/InputField'
import SwitchField from '../../../atoms/SwitchField'

/// /////////////////////////////////////////////////////////////////////////////
// 1) Make `id` optional: in “create” mode, the parent won’t supply an `id`.
/// /////////////////////////////////////////////////////////////////////////////
type ComponentProps = {
	isCreate: boolean;
	id?: number; // ← optional now
};

type Props = InjectedFormProps<IStoreItemForm, ComponentProps> & ComponentProps;

/// /////////////////////////////////////////////////////////////////////////////
// 2) We’ll keep a local piece of state for the “create‐transaction” modal:
//    - `isShowed`: controls whether the modal is visible
//    - When `isShowed === true`, we pass exactly those five fields to
//      the modal’s `transaction` prop. Otherwise, we pass `null`.
/// /////////////////////////////////////////////////////////////////////////////
export interface CreateTransactionPropsExtended extends CreateTransactionProps {
	isShowed: boolean;
}

const StoreItemForm = (props: Props) => {
	const {
		handleSubmit, invalid, pristine, isCreate, id
	} = props
	const dispatch = useDispatch()
	const { t } = useTranslation('components')

	/// /////////////////////////////////////////////////////////////////////////
	// 3) Grab the Redux-Form values. It might be `undefined` at first, so
	//    give every field a safe default—never let `storeItemForm` be `undefined`.
	/// /////////////////////////////////////////////////////////////////////////
	const rawFormValues = useSelector((state: any) => getFormValues(FORMS.STORE_ITEM_FORM)(state)) as Partial<IStoreItemForm> | undefined

	const storeItemForm: IStoreItemForm = {
		id: rawFormValues?.id ?? null,
		name: rawFormValues?.name ?? '',
		nameSlug: rawFormValues?.nameSlug ?? '',
		isLiquid: rawFormValues?.isLiquid ?? false,
		price: rawFormValues?.price ?? null,
		isReplaceable: rawFormValues?.isReplaceable ?? false,
		idealStockPercentage: rawFormValues?.idealStockPercentage ?? 0,
		activeJobs: rawFormValues?.activeJobs ?? 0,
		allAvailableStock: rawFormValues?.allAvailableStock ?? 0,
		updatedAt: rawFormValues?.updatedAt ?? null,
		amountOfStockInAddresses: rawFormValues?.amountOfStockInAddresses ?? []
	}

	/// /////////////////////////////////////////////////////////////////////////
	// 4) Local state for “create transaction” modal. `isShowed` is purely local;
	//    we’ll only pass the five required fields to the modal’s `transaction`.
	/// /////////////////////////////////////////////////////////////////////////
	const [createTransactionModal, setCreateTransactionModal] = useState<
		CreateTransactionPropsExtended
	>({
		amount: 1,
		isShowed: false,
		storeItemName: storeItemForm.name, // may start as '' but will update
		storeItemID: id ?? 0, // if undefined, we’ll ignore (modal won’t show)
		type: STORE_ITEM_TRANSACTION_TYPE.REGULAR,
		jobToID: null
	})

	/// /////////////////////////////////////////////////////////////////////////
	// 4a) Whenever the form’s `name` or `id` changes in Redux-Form (e.g. after init),
	//     keep `createTransactionModal.storeItemName` and `.storeItemID` in sync.
	/// /////////////////////////////////////////////////////////////////////////
	useEffect(() => {
		setCreateTransactionModal((prev) => ({
			...prev,
			storeItemName: storeItemForm.name,
			storeItemID: id ?? prev.storeItemID
		}))
	}, [storeItemForm.name, id])

	/// /////////////////////////////////////////////////////////////////////////
	// 5) Helpers to initialize this Redux-Form with “detail” vs “empty”:
	/// /////////////////////////////////////////////////////////////////////////
	const initDetailForm = (data: IStoreItemForm) => {
		dispatch(initialize(FORMS.STORE_ITEM_FORM, data))
	}

	/// /////////////////////////////////////////////////////////////////////////
	// 6) After a transaction is created successfully, re-fetch the parent store-item
	//    so that “updatedAt” / “allAvailableStock” / “amountOfStockInAddresses”
	//    fields all update. Then hide the modal.
	/// /////////////////////////////////////////////////////////////////////////
	const onStoreItemTransactionSuccess = () => {
		if (id == null) {
			// Should never happen, because we only open the modal when id != null
			return
		}

		dispatch(
			getStoreItem(id, (response) => {
				const d = response.data
				const initValues: IStoreItemForm = {
					id: d.id,
					isLiquid: d.isLiquid,
					price: d.price ?? null,
					isReplaceable: d.isReplaceable,
					idealStockPercentage: d.idealStockPercentage,
					activeJobs: d.activeJobs,
					updatedAt: moment(d.updatedAt).format('D. MMM YYYY HH:mm') || '-',
					allAvailableStock: d.allAvailableStock,
					amountOfStockInAddresses: d.amountOfStockInAddresses,
					nameSlug: d.nameSlug,
					name: d.name
				}

				// Re-initialize the Redux-Form with the latest detail data:
				initDetailForm(initValues)

				// Then close the modal:
				setCreateTransactionModal((prev) => ({
					...prev,
					isShowed: false
				}))
			})
		)
	}

	/// /////////////////////////////////////////////////////////////////////////
	// 7) Handle “+” button click: open the modal by setting isShowed = true.
	/// /////////////////////////////////////////////////////////////////////////
	const openCreateTransactionModal = () => {
		setCreateTransactionModal((prev) => ({
			...prev,
			isShowed: true
		}))
	}

	/// /////////////////////////////////////////////////////////////////////////
	// 8) RENDER:
	//    Note: We must use onSubmitCapture={handleSubmit} (not onFinish),
	//    so that Redux-Form’s `handleSubmit` actually runs.
	/// /////////////////////////////////////////////////////////////////////////
	return (
		<Form layout="vertical" onSubmitCapture={handleSubmit}>
			<Row gutter={16}>
				<Col span={18} className="grid">
					<div className="flex direction-col justify-start main-content">
						<h1>
							{isCreate
								? t('pages:Pridať skladovú položku')
								: t('pages:Upraviť skladovú položku')}
						</h1>

						{/* NAME */}
						<Field
							name="name"
							component={InputField}
							label={t('components:Názov položky')}
							size="large"
							required
						/>

						{/* PRICE */}
						<Field
							name="price"
							component={InputField}
							label={t('components:Cena za kus')}
							size="large"
							type="number"
							min="0"
							step="0.01"
							nullValue={null}
							required
						/>

						{/* IDEAL STOCK % */}
						<Field
							name="idealStockPercentage"
							component={InputNumberField}
							label={
								<Popover
									content={
										<div>
											<p>
												<span>
                          -{' '}
													{t(
														'components:ideálna percentuálna hodnota skladových zásob v pomere k počtu vchodov'
													)}
												</span>
												<span>
													<b> ({storeItemForm.activeJobs})</b>
												</span>
											</p>
											<p>
												- príklad: ak bude počet vchodov 100 – a do stĺpčeka{' '}
												<b>{storeItemForm.name}</b> dám, že to musí byť 120 %
												(teda 120 kusov). Keď klesne počet kusov na sklade pod 120 kusov, riadok sa vyčervení.
											</p>
											<p>
												- príklad 2: ak bude počet vchodov 100 – a{' '}
												<b>{storeItemForm.name}</b> musí byť 20 % (teda 20 kusov). Keď klesne počet kusov pod 20 kusov, riadok sa vyčervení.
											</p>
										</div>
									}
								>
									<div>
										<span>
											{t('components:(%) Ideálna percentuálna hodnota skladových zásob v pomere k počtu vchodov')}
										</span>
										<span> ({storeItemForm.activeJobs})</span>
									</div>
								</Popover>
							}
							size="large"
							type="number"
							min={0}
							precision={0}
							step={1}
							max={1000}
							required
						/>

						{/* IS LIQUID */}
						<Field
							name="isLiquid"
							component={SwitchField}
							label={t('components:Je tekutina')}
							size="large"
							required
						/>
					</div>
				</Col>

				<Col span={6} className="grid">
					<div className="flex direction-col justify-start sidebar-content">
						{/* UPDATED AT (only in edit mode) */}
						{!isCreate && (
							<Field
								name="updatedAt"
								component={TextField}
								disabled
								label={t('components:Dátum poslednej úpravy')}
							/>
						)}

						<div className="flex direction-col justify-center">
							<Button
								icon={<SaveOutlined />}
								htmlType="submit"
								disabled={pristine || invalid}
								type="primary"
								className="mt-4"
							>
								{t('pages:Uložiť')}
							</Button>
						</div>
					</div>
				</Col>
			</Row>

			{/* SHOW “+” BUTTON & STOCK TABLE only if we have a real `id` (edit mode) */}
			{id != null && (
				<Row className="mt-4" gutter={16}>
					<Col span={24} className="grid">
						<div className="flex direction-col justify-start main-content">
							<div className="flex justify-between">
								<div className="flex justify-between">
									<h1>
										{t('pages:Celkovy počet kusov na pobočke')}
										<Tag
											className="ml-2"
											color={colorHash(STORE_TYPE.VIRTUAL_STORE)}
										>
											{getPriorityForStoreType(
												STORE_TYPE.VIRTUAL_STORE,
												t
											)}
										</Tag>
										<span className="pr-2">+</span>
										<Tag color={colorHash(STORE_TYPE.JOB_STORE)}>
											{getPriorityForStoreType(STORE_TYPE.JOB_STORE, t)}
										</Tag>
									</h1>
									<h2 className="ml-3">
										{storeItemForm.allAvailableStock}
									</h2>
								</div>

								<Tooltip title={t('pages:Pridaj/odober položky')}>
									<div
										style={{
											width: '50px',
											height: '50px',
											backgroundColor: 'orange',
											borderRadius: '50%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
											cursor: 'pointer'
										}}
										onClick={openCreateTransactionModal}
									>
										<PlusOutlined
											style={{ fontSize: '24px', color: '#fff' }}
										/>
									</div>
								</Tooltip>
							</div>

							<AddressWithStocksTable
								itemID={id}
								amountOfStockInAddresses={
									storeItemForm.amountOfStockInAddresses
								}
							/>
						</div>
					</Col>
				</Row>
			)}

			{/* ===========================================================================
         9) CreateStoreItemTransactionModal expects:
            Props = {
              isVisible: boolean;
              transaction: CreateTransactionProps | null;
              onCancel: Function;
              onSuccess: Function;
              classNameBtn?: string;
              width?: number; // (ModalProps already includes width)
            }
         =========================================================================== */}

			<CreateStoreItemTransactionModal
				// 1) Pass `isVisible` based on `isShowed`
				isVisible={createTransactionModal.isShowed}

				// 2) Only pass a CreateTransactionProps object when isShowed = true.
				//    Otherwise, pass `null`.
				transaction={
					createTransactionModal.isShowed
						? {
							amount: createTransactionModal.amount,
							type: createTransactionModal.type,
							storeItemName: createTransactionModal.storeItemName,
							storeItemID: createTransactionModal.storeItemID,
							jobToID: createTransactionModal.jobToID ?? null
						}
						: null
				}

				// 3) onCancel simply hides the modal
				onCancel={() => setCreateTransactionModal((prev) => ({
					...prev,
					isShowed: false
				}))
				}

				// 4) onSuccess = after a transaction succeeds, re-fetch parent detail
				onSuccess={onStoreItemTransactionSuccess}

				// 5) (Optional) You can pass a custom classNameBtn or width if needed:
				classNameBtn="ant-btn-primary"
				width={500}
			/>
		</Form>
	)
}

// Wrap with reduxForm, using `id` as an optional prop
const WrappedForm = reduxForm<IStoreItemForm, ComponentProps>({
	form: FORMS.STORE_ITEM_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateForm
})(StoreItemForm)

export default WrappedForm
